import { parseVoiceDateRange } from "../../voice/ownerVoiceParsers";
import {
  OWNER_JARVIS_CONFIRMATION,
  OWNER_JARVIS_OPERATION_KIND,
  defineOwnerJarvisCapability,
} from "../ownerJarvisCapabilityContract";
import { includesAny, spokenDate, spokenDuration } from "../ownerJarvisUtils";
import { captureOwnerInboxEntry } from "../../../../../services/ownerInbox.service";

function stripJarvisMemoryCommand(raw = "") {
  const clean = String(raw || "").trim();
  const stripped = clean.replace(/^\s*(?:jarvis[,:]?\s*)?(?:anotá|anota|anotame|capturá|captura|guardá esto|guarda esto|dejá en inbox|deja en inbox|guardá en memoria|guarda en memoria|guardá como recuerdo(?: que)?|guarda como recuerdo(?: que)?|acordate de|recordá(?: que)?|recorda(?: que)?)(?:\s+|$)/i, "").trim();
  return stripped !== clean ? stripped : "";
}

function localNoonIso(dateKey) {
  const date = new Date(`${dateKey}T12:00:00`);
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
}

function memorySpeech(memories, range) {
  const summary = memories?.summary || {};
  const items = memories?.items || [];
  if (!items.length) return `Entre ${spokenDate(range.from)} y ${spokenDate(range.to)} no encontré actividad guardada todavía.`;
  const parts = [`Entre ${spokenDate(range.from)} y ${spokenDate(range.to)} encontré ${items.length} registros.`];
  if (summary.workMinutes) parts.push(`Trabajaste ${spokenDuration(summary.workMinutes)}.`);
  if (summary.completedCommitments) parts.push(`Completaste ${summary.completedCommitments} tareas.`);
  if (summary.completedRoutines) parts.push(`Cumpliste ${summary.completedRoutines} rutinas.`);
  if (summary.interactions) parts.push(`Registraste ${summary.interactions} contactos.`);
  const highlights = [...items].reverse().map((item) => String(item.title || "").trim()).filter(Boolean).slice(0, 4);
  if (highlights.length) parts.push(`Recuerdo: ${highlights.join("; ")}.`);
  return parts.join(" ");
}

export function createOwnerJarvisMemoryCaptureCapability({ ownerApi }) {
  return defineOwnerJarvisCapability({
    id: "memory-capture",
    domain: "memory",
    description: "Captura cualquier nota explícita de Jarvis en el mismo Inbox Owner, sin crear un estado paralelo.",
    reads: ["owner.lifeEntries"],
    identify({ raw }) {
      const memory = stripJarvisMemoryCommand(raw);
      if (!memory) return null;
      return { operationId: "capture", payload: { memory, memoryRange: parseVoiceDateRange(raw) } };
    },
    operations: [{
      id: "capture",
      kind: OWNER_JARVIS_OPERATION_KIND.SAFE_WRITE,
      // Compatibilidad: este comando ya era una orden explícita de guardado y no pedía doble confirmación.
      confirmation: OWNER_JARVIS_CONFIRMATION.NEVER,
      invalidates: ["today"],
      async execute({ payload }) {
        const { memory, memoryRange } = payload;
        const response = await captureOwnerInboxEntry(ownerApi, {
          text: memory,
          inputSource: "voice",
          occurredAt: memoryRange ? localNoonIso(memoryRange.from) : new Date().toISOString(),
          structuredData: {
            jarvis: true,
            captureType: "memory",
            capturedFrom: "owner360-loop",
            ...(memoryRange ? { memoryRange: { from: memoryRange.from, to: memoryRange.to } } : {}),
          },
        });
        const hasProposal = Boolean(response?.entry?.structuredData?.captureInterpretation?.hasSuggestions);
        const baseMessage = memoryRange ? `Listo. Lo dejé en tu Inbox con fecha ${spokenDate(memoryRange.from)}.` : `Listo. Lo dejé en tu Inbox: ${memory}.`;
        return {
          handled: true,
          message: hasProposal
            ? `${baseMessage} Detecté datos para revisar como propuesta, sin crear vínculos automáticamente.`
            : baseMessage,
        };
      },
      errors: { execute: "No pude guardar ese recuerdo ahora. Probá de nuevo." },
    }],
    errors: {
      identify: "No pude interpretar qué querías guardar.",
      unsupported: "Ese tipo de cambio de memoria todavía no está habilitado.",
    },
  });
}

export function createOwnerJarvisMemoryRecallCapability({ ownerApi }) {
  return defineOwnerJarvisCapability({
    id: "memory-recall",
    domain: "memory",
    description: "Consulta memoria operativa dentro de un rango temporal explícito.",
    reads: ["owner.memories"],
    identify({ raw, text }) {
      const range = parseVoiceDateRange(raw);
      if (!range || !includesAny(text, ["que hice", "que paso", "recuerdos", "recordame", "resumen de", "que estuve haciendo"])) return null;
      return { operationId: "recall", payload: { range } };
    },
    operations: [{
      id: "recall",
      kind: OWNER_JARVIS_OPERATION_KIND.READ,
      confirmation: OWNER_JARVIS_CONFIRMATION.NEVER,
      async execute({ payload }) {
        const response = await ownerApi.memories({ from: payload.range.from, to: payload.range.to });
        return { handled: true, message: memorySpeech(response?.memories, payload.range) };
      },
      errors: { execute: "No pude consultar tus recuerdos ahora. Probá de nuevo." },
    }],
    errors: { unsupported: "Esa operación de memoria todavía no está habilitada." },
  });
}
